import React, { ReactNode, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Link,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { makeStyles } from "@mui/styles";
import { v2theme } from "../../../styles/Theme";
import CloseIcon from "@mui/icons-material/Close";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import { generateGtmEvent } from "../../../Helpers";

const useStyle = makeStyles((theme) => ({
  modal: {
    position: "absolute",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  modalTitle: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "20px",
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  inputText: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
    },
  },
  endAdornment: {
    "& .MuiInputAdornment-root": {
      maxHeight: "unset",
      maxWidth: "unset",
      width: 48,
      height: 48,
      marginLeft: "0px !important",
    },
    position: "relative",
    right: -10,
    width: 48,
    height: 48,
  },
  endAdornmentIcon: {
    transform: "translateY(-2px)",
    color: theme.palette.text.secondary,
  },
}));

interface DateRangeSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onDateRangeChange: (startDate: Date | null, endDate: Date | null) => void;
  startDate: Date;
  endDate: Date;
}

export const DateRangeSelectorModal = (props: DateRangeSelectorModalProps) => {
  const { open, onClose, onDateRangeChange, startDate, endDate } = props;
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(startDate);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(endDate);
  const classes = useStyle();
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="insights-date-range-selector-modal">
      <Card sx={{ width: "600px" }} className={classes.modal}>
        <CardHeader
          sx={{ padding: "16px 24px" }}
          title={
            <Typography
              id="edit-account-modal-title"
              variant="h6"
              className={classes.modalTitle}>
              Custom dates
            </Typography>
          }
          action={
            <Tooltip
              placement="bottom-end"
              title="Close"
              arrow
              role="tooltip"
              describeChild>
              <IconButton
                onClick={() => {
                  onClose();
                  generateGtmEvent({
                    event: "modal",
                    action: "close",
                    name: "onclick",
                    type: "click",
                    region: "main content",
                    section: "custom dates",
                    text: "close cross",
                  });
                }}
                aria-label="close edit user account"
                className={classes.closeButton}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          }
        />
        <Divider />
        <CardContent
          sx={{
            padding: "24px 32px",
            maxHeight: "400px",
            overflow: "auto",
          }}>
          <Typography
            component={"span"}
            sx={{
              font: "normal normal bold 24px/27px proxima-soft",
              color: "#000000DE",
              letterSpacing: "0.18px",
            }}>
            Choose date range to filter by
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", marginTop: "32px" }}>
            <Grid container spacing={6}>
              <Grid item xs={6}>
                <DatePicker
                  className={classes.inputText}
                  label="Start date"
                  value={selectedStartDate}
                  onChange={(date) => {
                    setSelectedStartDate(date);
                  }}
                  onAccept={(value) => {
                    generateGtmEvent({
                      event: "select",
                      action: "click",
                      name: "onclick",
                      type: "select date",
                      region: "main content",
                      section: `start date ^ calendar icon`,
                      text: `${value}`,
                    });
                  }}
                  onOpen={() => {
                    generateGtmEvent({
                      event: "collapse",
                      action: "open",
                      name: "onclick",
                      type: "click",
                      region: "main content",
                      section: `start date`,
                      text: "calendar icon",
                      component: "class info",
                    });
                  }}
                  renderInput={(params) => {
                    let endAdornment: ReactNode = params.InputProps!.endAdornment;
                    return (
                      <TextField
                        {...params}
                        required
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <Tooltip
                              placement="bottom"
                              title="Open calendar"
                              arrow
                              role="tooltip"
                              describeChild>
                              <Box className={classes.endAdornment}>{endAdornment}</Box>
                            </Tooltip>
                          ),
                        }}
                      />
                    );
                  }}
                  PopperProps={{
                    sx: {
                      zIndex: 9999,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  className={classes.inputText}
                  label="End date"
                  value={selectedEndDate}
                  onChange={(date) => {
                    setSelectedEndDate(date);
                  }}
                  onAccept={(value) => {
                    generateGtmEvent({
                      event: "select",
                      action: "click",
                      name: "onclick",
                      type: "select date",
                      region: "main content",
                      section: `end date ^ calendar icon`,
                      text: `${value}`,
                    });
                  }}
                  onOpen={() => {
                    generateGtmEvent({
                      event: "collapse",
                      action: "open",
                      name: "onclick",
                      type: "click",
                      region: "main content",
                      section: `end date`,
                      text: "calendar icon",
                      component: "class info",
                    });
                  }}
                  minDate={selectedStartDate}
                  renderInput={(params) => {
                    let endAdornment: ReactNode = params.InputProps!.endAdornment;
                    return (
                      <TextField
                        {...params}
                        required
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <Tooltip
                              placement="bottom"
                              title="Open calendar"
                              arrow
                              role="tooltip"
                              describeChild>
                              <Box className={classes.endAdornment}>{endAdornment}</Box>
                            </Tooltip>
                          ),
                        }}
                      />
                    );
                  }}
                  PopperProps={{
                    sx: {
                      zIndex: 9999,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </CardContent>
        <Divider />
        <CardActions sx={{ padding: "24px 48px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}>
            <Link
              onClick={() => {
                onClose();
                generateGtmEvent({
                  event: "link",
                  action: "click",
                  name: "onclick",
                  type: "internal link",
                  region: "main content",
                  section: "custom dates",
                  text: "clear",
                });
              }}
              variant="body1">
              Clear
            </Link>
            <Link
              onClick={() => {
                onDateRangeChange(selectedStartDate, selectedEndDate);
                generateGtmEvent({
                  event: "link",
                  action: "click",
                  name: "onclick",
                  type: "internal link",
                  region: "main content",
                  section: "custom dates",
                  text: "apply",
                });
              }}
              underline="none">
              <Button
                size="large"
                variant="contained"
                sx={{
                  borderRadius: "16px",
                  width: "180px",
                  height: "62px",
                  padding: "0px",
                  "&:hover": {
                    transform: "none",
                  },
                }}>
                Apply
              </Button>
            </Link>
          </Box>
        </CardActions>
      </Card>
    </Modal>
  );
};
